import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import { BasicMutationOptions } from "../queryClient";

type SignInResponseData = { accessToken: string; refreshToken: string };
type SignInRequestParams = {
  email: string;
  password: string;
  tenant?: string;
};

type ActivateAccountRequestParams = {
  confirmationKey: string;
  newPassword: string;
  userId: string;
  tenant: string;
};
type ActivateAccountResponseData = unknown;

export const useSignInMutation = (mutationOptions?: BasicMutationOptions<SignInResponseData, AxiosError>) => {
  const { publicApiClient } = useApiClient();

  return useMutation<SignInResponseData, AxiosError, SignInRequestParams>({
    mutationFn: async (data: SignInRequestParams) =>
      (await publicApiClient.post(`${data.tenant ? "/tenant/" + data.tenant : ""}/auth/login`, data)).data,
    mutationKey: ["signIn"],
    ...mutationOptions,
  });
};

export const useActivateAccountMutation = (
  mutationOptions?: BasicMutationOptions<ActivateAccountResponseData, AxiosError>,
) => {
  const { publicApiClient } = useApiClient();

  return useMutation<ActivateAccountResponseData, AxiosError, ActivateAccountRequestParams>({
    mutationFn: async ({ tenant, userId, ...requestData }: ActivateAccountRequestParams) =>
      await publicApiClient.post(`/tenant/${tenant}/user/${userId}/activate-account`, requestData),
    mutationKey: ["activateAccount"],
    ...mutationOptions,
  });
};
export const useRegisterUserMutation = (
  mutationOptions?: BasicMutationOptions<RegisterUserResponseData, AxiosError>,
) => {
  const { publicApiClient } = useApiClient();

  return useMutation<RegisterUserResponseData, AxiosError, RegisterUserRequestParams>({
    mutationFn: async ({ tenant, ...requestData }: RegisterUserRequestParams) =>
      (await publicApiClient.post(`/tenant/${tenant}/user`, requestData)).data,
    mutationKey: ["registerUser"],
    ...mutationOptions,
  });
};

export const useRequestResetMutation = (
  mutationOptions?: BasicMutationOptions<RequestResetResponseData, AxiosError>,
) => {
  const { publicApiClient } = useApiClient();

  return useMutation<RequestResetResponseData, AxiosError, RequestResetRequestParams>({
    mutationFn: async ({ tenant, ...requestData }: RequestResetRequestParams) =>
      (await publicApiClient.post(`/tenant/${tenant}/user-reset-password`, requestData)).data,
    mutationKey: ["requestReset"],
    ...mutationOptions,
  });
};

export const useConfirmEmailQuery = ({ tenant, userId, confirmationKey }: ConfirmEmailRequestData) => {
  const { publicApiClient } = useApiClient();

  return useQuery<ConfirmEmailRequestData, AxiosError>({
    queryFn: async () =>
      (await publicApiClient.get(`/tenant/${tenant}/user/${userId}/confirm-email/${confirmationKey}`)).data,
    queryKey: ["login"],
  });
};

type ConfirmEmailRequestData = {
  tenant: string;
  userId: string;
  confirmationKey: string;
};

type RegisterUserResponseData = Record<string, never>;
type RegisterUserRequestParams = {
  email: string;
  password: string;
  tenant: string;
};

type RequestResetResponseData = unknown;

type RequestResetRequestParams = {
  email: string;
  tenant: string;
};
